import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import './ChartProgress.scss';

// The ring's own viewBox units
const RING_SIZE = 24;
const RING_RADIUS = 9;

/** How many parts are charted: a ring filled that far round, and a label for the percentage. */
const ChartProgress = ({ done, total, label, doneIcon, className }) => {
  const { t } = useTranslation();
  const centre = RING_SIZE / 2;
  const circumference = 2 * Math.PI * RING_RADIUS;
  const percent = total > 0 ? Math.floor((done / total) * 100) : 0;
  const isComplete = total > 0 && done === total;

  return (
    <div
      className={classNames('ChartProgress', className)}
      role="img"
      aria-label={t('charts.progressLabel', { done, total })}
    >
      {isComplete && doneIcon ? (
        <span className="done-icon" aria-hidden="true">
          {doneIcon}
        </span>
      ) : (
        <svg className="ring" viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} aria-hidden="true">
          <circle className="track" cx={centre} cy={centre} r={RING_RADIUS} />
          <circle
            className="fill"
            cx={centre}
            cy={centre}
            r={RING_RADIUS}
            strokeDasharray={`${(percent / 100) * circumference} ${circumference}`}
            // From the top, going round clockwise
            transform={`rotate(-90 ${centre} ${centre})`}
          />
        </svg>
      )}
      <span className="message" aria-hidden="true">
        {label ? label(percent) : t('charts.percentCompleted', { percent })}
      </span>
    </div>
  );
};

ChartProgress.propTypes = {
  done: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  /** The text beside the ring, from the percentage */
  label: PropTypes.func,
  /** Shown in place of the full ring once everything is charted */
  doneIcon: PropTypes.node,
  className: PropTypes.string,
};

export default ChartProgress;
